import { normalizeText, normalizeUnit } from './schema';
import { validateProductTransfer } from './validation';

export const TRANSFER_FILE_MAX_BYTES = 3_500_000;
export const TRANSFER_MAX_NEW_PRODUCTS = 440;
const NEW_ID_PREFIX = '@new:';

const utf8Bytes = (value) => {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
  if (typeof Buffer !== 'undefined') return Buffer.byteLength(text, 'utf8');
  return unescape(encodeURIComponent(text)).length;
};

const activeProduct = (product) => product && product.deleted !== true && product.id;
const plainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const productType = (product) => String(product?.type || 'final').trim().toLowerCase();
const categoryValue = (product) => typeof product?.category === 'string' ? product.category.trim() : String(product?.category?.value || '').trim();
const cleanCode = (value) => String(value ?? '').trim();
const compatible = (source, target) => {
  if (!activeProduct(target)) return false;
  if (productType(source) !== productType(target)) return false;
  if (productType(source) === 'insumo' && normalizeUnit(source.unit) !== normalizeUnit(target.unit)) return false;
  return true;
};

const isPromotionProduct = (product) => product?.config?.promotion?.enabled === true;
const isPromotionBaseCompatible = (product) => activeProduct(product) && plainObject(product?.config?.recipe) && !isPromotionProduct(product);
const nameIncludes = (product, token) => normalizeText(product?.name).includes(normalizeText(token));
const nameStarts = (product, token) => normalizeText(product?.name).startsWith(normalizeText(token));

const tokenSimilarity = (a, b) => {
  const left = new Set(normalizeText(a).split(/\s+/).filter(Boolean));
  const right = new Set(normalizeText(b).split(/\s+/).filter(Boolean));
  if (!left.size || !right.size) return 0;
  let shared = 0;
  left.forEach((token) => { if (right.has(token)) shared += 1; });
  return shared / new Set([...left, ...right]).size;
};

export function scoreImportCandidate(source, target) {
  if (!compatible(source, target)) return null;
  let score = 0;
  const reasons = [];
  const sourceName = normalizeText(source.name);
  const targetName = normalizeText(target.name);
  const sourceBarcode = cleanCode(source.barcode);
  const targetBarcode = cleanCode(target.barcode);
  const sourceCode = cleanCode(source.code);
  const targetCode = cleanCode(target.code);
  const sourceSku = cleanCode(source.sku);
  const targetSku = cleanCode(target.sku);

  if (sourceBarcode && targetBarcode && sourceBarcode === targetBarcode) { score += 120; reasons.push('Mismo código de barras'); }
  if (sourceCode && targetCode && sourceCode === targetCode) { score += 70; reasons.push('Mismo code'); }
  if (sourceSku && targetSku && sourceSku === targetSku) { score += 70; reasons.push('Mismo SKU'); }
  if (sourceName && targetName && sourceName === targetName) { score += 80; reasons.push('Mismo nombre'); }
  else if (sourceName && targetName) {
    const similarity = tokenSimilarity(sourceName, targetName);
    if ((sourceName.length >= 4 && targetName.includes(sourceName)) || (targetName.length >= 4 && sourceName.includes(targetName))) {
      score += 45; reasons.push('Nombre relacionado');
    } else if (similarity >= 0.5) {
      score += Math.round(similarity * 40); reasons.push('Nombre parecido');
    }
  }
  if (normalizeText(source.category) && normalizeText(source.category) === normalizeText(categoryValue(target))) { score += 10; reasons.push('Misma categoría'); }
  if (productType(source) === 'insumo' && normalizeUnit(source.unit) === normalizeUnit(target.unit)) { score += 5; reasons.push('Misma unidad'); }
  if (score < 35) return null;
  return { id: target.id, name: target.name || target.id, score, reasons, category: categoryValue(target), unit: target.unit || '', barcode: cleanCode(target.barcode), code: cleanCode(target.code), sku: cleanCode(target.sku) };
}

export function inspectProductTransferText(text) {
  if (utf8Bytes(text) > TRANSFER_FILE_MAX_BYTES) throw new Error('El archivo supera el límite seguro de 3.500.000 bytes para esta transferencia.');
  let parsed;
  try { parsed = JSON.parse(text); }
  catch { throw new Error('No se pudo leer este archivo de transferencia.'); }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Este archivo no corresponde a una transferencia compatible de ValenPOS.');
  if (parsed.format !== 'valenpos.product-transfer') throw new Error('Este archivo no corresponde a una transferencia compatible de ValenPOS.');
  if (parsed.schemaVersion !== 1) throw new Error('Este archivo pertenece a una versión de transferencia no compatible.');
  const validation = validateProductTransfer(parsed);
  if (!validation.valid) throw new Error(`No se pudo validar este archivo de transferencia:\n${validation.errors.join('\n')}`);
  return { kind: 'transfer', value: parsed };
}

export async function inspectProductTransferFile(file) {
  if (!file) throw new Error('Selecciona un archivo JSON.');
  if (!String(file.name || '').toLowerCase().endsWith('.json')) throw new Error('Selecciona un archivo .json de transferencia ValenPOS.');
  if (Number(file.size || 0) > TRANSFER_FILE_MAX_BYTES) throw new Error('El archivo supera el límite seguro de 3.500.000 bytes para esta transferencia.');
  const text = await file.text();
  return inspectProductTransferText(text);
}

const getNodeHardRefs = (node) => {
  const refs = [];
  const config = node?.data?.config || {};
  (config.recipe?.baseItems || []).forEach((item) => refs.push(item.productRef));
  (config.extraItems || []).forEach((item) => refs.push(item.productRef));
  if (config.baseInsumoRef) refs.push(config.baseInsumoRef);
  if (config.promotion?.sourceProductRef) refs.push(config.promotion.sourceProductRef);
  (config.recipe?.choiceGroups || []).forEach((group) => { if (group.defaultProductRef) refs.push(group.defaultProductRef); });
  return [...new Set(refs.filter(Boolean))];
};

const getNodeSelectorRefs = (node) => [...new Set((node?.data?.config?.recipe?.choiceGroups || []).map((group) => group.selectorRef).filter(Boolean))];

export function analyzeProductTransfer(file, catalog = []) {
  const validation = validateProductTransfer(file);
  if (!validation.valid) throw new Error(validation.errors.join('\n'));
  // ProductsContext can transiently contain the same Firestore document more than once
  // while cached/remote pages are reconciled. The import assistant must present one
  // destination option per real document, never duplicate suggestions for the same id.
  const localProducts = [...new Map(catalog.filter(activeProduct).map((product) => [product.id, product])).values()];
  const localById = new Map(localProducts.map((product) => [product.id, product]));
  const roots = new Set(file.products.map((node) => node.ref));
  const allNodes = [...file.products, ...file.dependencies.products];
  const nodeByRef = new Map(allNodes.map((node) => [node.ref, node]));

  const nodes = allNodes.map((node) => {
    const options = localProducts.filter((product) => compatible(node.data, product)).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' }));
    const allCandidates = options.map((product) => scoreImportCandidate(node.data, product)).filter(Boolean).sort((a, b) => b.score - a.score || a.name.localeCompare(b.name, 'es'));
    const candidates = allCandidates.slice(0, 6);
    const sourceBarcode = cleanCode(node.data.barcode);
    const barcodeConflicts = sourceBarcode ? localProducts.filter((product) => cleanCode(product.barcode) === sourceBarcode).map((product) => ({ id: product.id, name: product.name || product.id, category: categoryValue(product), unit: product.unit || '', barcode: cleanCode(product.barcode), code: cleanCode(product.code), sku: cleanCode(product.sku), compatible: compatible(node.data, product) })) : [];
    const barcodeMatches = allCandidates.filter((candidate) => candidate.reasons.includes('Mismo código de barras'));
    const exactIdentityMatches = allCandidates.filter((candidate) => {
      if (!candidate.reasons.includes('Mismo nombre') || !candidate.reasons.includes('Misma categoría')) return false;
      return productType(node.data) !== 'insumo' || candidate.reasons.includes('Misma unidad');
    });
    const uniqueBarcodeMatches = sourceBarcode && barcodeConflicts.length === 1 ? barcodeMatches : [];
    const highConfidenceMatches = uniqueBarcodeMatches.length ? uniqueBarcodeMatches : (!sourceBarcode || barcodeConflicts.length === 0 ? exactIdentityMatches : []);
    const ambiguousBarcode = !!sourceBarcode && barcodeConflicts.length > 1;
    const recommendedCandidateId = !ambiguousBarcode && highConfidenceMatches.length === 1 ? highConfidenceMatches[0].id : '';
    const recommendationKind = recommendedCandidateId ? (uniqueBarcodeMatches.length ? 'barcode' : 'identity') : '';
    return {
      ref: node.ref,
      sourceProductId: node.sourceProductId,
      data: node.data,
      warnings: node.warnings || [],
      role: roots.has(node.ref) ? 'selected' : 'dependency',
      candidates: candidates.map((candidate) => ({ ...candidate, recommended: candidate.id === recommendedCandidateId })),
      barcodeConflicts,
      recommendedCandidateId,
      recommendationKind,
      highConfidenceCandidateIds: ambiguousBarcode ? barcodeConflicts.map((candidate) => candidate.id) : highConfidenceMatches.map((candidate) => candidate.id),
      ambiguousHighConfidence: ambiguousBarcode || highConfidenceMatches.length > 1,
      compatibleOptions: options.map((product) => ({ id: product.id, name: product.name || product.id, category: categoryValue(product), unit: product.unit || '', barcode: cleanCode(product.barcode), code: cleanCode(product.code), sku: cleanCode(product.sku) })),
      hardRefs: getNodeHardRefs(node),
      selectorRefs: getNodeSelectorRefs(node),
    };
  });

  const selectors = file.dependencies.selectors.map((selector) => {
    const categoryMap = new Map();
    localProducts.forEach((product) => {
      if (productType(product) !== 'insumo' || normalizeUnit(product.unit) !== selector.unit) return;
      const category = categoryValue(product);
      if (!category) return;
      const key = normalizeText(category);
      const current = categoryMap.get(key) || { category, count: 0, members: [], exactSource: key === normalizeText(selector.sourceCategory) };
      current.count += 1;
      current.members.push({ id: product.id, name: product.name || product.id, unit: product.unit || '' });
      categoryMap.set(key, current);
    });
    const sourceKey = normalizeText(selector.sourceCategory);
    if (!categoryMap.has(sourceKey)) categoryMap.set(sourceKey, { category: selector.sourceCategory, count: 0, members: [], exactSource: true });
    const options = [...categoryMap.values()].sort((a, b) => Number(b.exactSource) - Number(a.exactSource) || a.category.localeCompare(b.category, 'es', { sensitivity: 'base' }));
    return {
      ...selector,
      sourceMembers: selector.memberRefs.map((ref) => ({ ref, name: nodeByRef.get(ref)?.data?.name || ref })),
      categoryOptions: options,
      suggestedCategory: options.find((option) => option.exactSource && option.count > 0)?.category || '',
    };
  });

  const fileWarnings = nodes.flatMap((node) => node.warnings.map((warning) => ({ ...warning, product: node.data.name, ref: node.ref })));
  return { file, nodes, selectors, localProducts, localById, nodeByRef, roots: [...roots], fileWarnings };
}

const destinationIdFor = (ref, decisions, localById, errors, context) => {
  const decision = decisions[ref];
  if (!decision) { errors.push(`${context}: falta decidir qué hacer con ${ref}`); return ''; }
  if (decision.action === 'omit') { errors.push(`${context}: la dependencia ${ref} no puede omitirse`); return ''; }
  if (decision.action === 'create') return `${NEW_ID_PREFIX}${ref}`;
  if (decision.action === 'existing') {
    if (!decision.targetId || !localById.has(decision.targetId)) { errors.push(`${context}: producto existente inválido para ${ref}`); return ''; }
    return decision.targetId;
  }
  errors.push(`${context}: acción no soportada para ${ref}`);
  return '';
};

const clone = (value) => value == null ? value : JSON.parse(JSON.stringify(value));

const destinationFor = (ref, decisions, localById, nodeByRef, errors, context) => {
  const decision = decisions[ref];
  const sourceName = nodeByRef.get(ref)?.data?.name || '';
  if (!decision) { errors.push(`${context}: falta decidir qué hacer con ${ref}`); return { id: '', name: sourceName }; }
  if (decision.action === 'omit') { errors.push(`${context}: la dependencia ${ref} no puede omitirse`); return { id: '', name: sourceName }; }
  if (decision.action === 'create') return { id: `${NEW_ID_PREFIX}${ref}`, name: sourceName };
  if (decision.action === 'existing') {
    const target = localById.get(decision.targetId);
    if (!target) { errors.push(`${context}: producto existente inválido para ${ref}`); return { id: '', name: sourceName }; }
    return { id: target.id, name: target.name || sourceName || target.id };
  }
  errors.push(`${context}: acción no soportada para ${ref}`);
  return { id: '', name: sourceName };
};

function reconstructCreateNode(node, decisions, selectorDecisions, analysis, errors) {
  const data = clone(node.data);
  const config = data.config || null;
  if (!config) return { ...data, stock: 0, image: '' };
  const remapItem = (item, label) => { const target = destinationFor(item.productRef, decisions, analysis.localById, analysis.nodeByRef, errors, `${data.name}: ${label}`); return { productId: target.id, name: target.name || item.name || '', qty: item.qty, unit: item.unit }; };
  if (config.recipe) {
    config.recipe.baseItems = (config.recipe.baseItems || []).map((item) => remapItem(item, item.name || 'ingrediente'));
    config.recipe.choiceGroups = (config.recipe.choiceGroups || []).map((group) => {
      const selectorDecision = selectorDecisions[group.selectorRef];
      if (!selectorDecision?.targetCategory) errors.push(`${data.name}: falta seleccionar categoría destino para ${group.label || group.id}`);
      const rebuilt = { id: group.id, label: group.label, min: group.min, max: group.max, perPickQty: group.perPickQty, unit: group.unit, fromCategory: selectorDecision?.targetCategory || '' };
      if (group.defaultProductRef) rebuilt.defaultProductId = destinationIdFor(group.defaultProductRef, decisions, analysis.localById, errors, `${data.name}: opción predeterminada`);
      return rebuilt;
    });
  }
  if (config.baseInsumoRef) { const target = destinationFor(config.baseInsumoRef, decisions, analysis.localById, analysis.nodeByRef, errors, `${data.name}: base de helado`); config.baseInsumoId = target.id; config.baseInsumoName = target.name || config.baseInsumoName || ''; delete config.baseInsumoRef; }
  if (config.extraItems) config.extraItems = config.extraItems.map((item) => remapItem(item, item.name || 'extra'));
  if (config.promotion?.sourceProductRef) {
    const target = destinationFor(config.promotion.sourceProductRef, decisions, analysis.localById, analysis.nodeByRef, errors, `${data.name}: promoción`);
    const quantity = Number(config.promotion.quantity || 0);
    const sourceProductName = target.name || config.promotion.sourceProductName || '';
    config.promotion = {
      enabled: config.promotion.enabled === true,
      weekdays: Array.isArray(config.promotion.weekdays) ? [...config.promotion.weekdays] : [],
      components: [{ sourceProductId: target.id, sourceProductName, quantity }],
      sourceProductId: target.id,
      sourceProductName,
      quantity,
    };
  }
  return { ...data, config, stock: 0, image: '' };
}

const collectEffectiveRefs = (analysis, decisions, selectorDecisions, errors) => {
  const effective = new Set();
  const usedSelectors = new Set();
  const queue = [];
  analysis.roots.forEach((ref) => {
    const decision = decisions[ref];
    if (!decision) errors.push(`Producto seleccionado ${analysis.nodeByRef.get(ref)?.data?.name || ref}: falta decidir una acción`);
    else if (decision.action !== 'omit') { effective.add(ref); if (decision.action === 'create') queue.push(ref); }
  });
  for (let i = 0; i < queue.length; i += 1) {
    const ref = queue[i];
    const node = analysis.nodes.find((item) => item.ref === ref);
    if (!node) continue;
    node.hardRefs.forEach((depRef) => {
      const decision = decisions[depRef];
      if (!decision) { errors.push(`${node.data.name}: falta resolver ${analysis.nodeByRef.get(depRef)?.data?.name || depRef}`); return; }
      if (decision.action === 'omit') { errors.push(`${node.data.name}: no puedes omitir ${analysis.nodeByRef.get(depRef)?.data?.name || depRef}`); return; }
      if (!effective.has(depRef)) {
        effective.add(depRef);
        if (decision.action === 'create') queue.push(depRef);
      }
    });
    node.selectorRefs.forEach((selectorRef) => {
      usedSelectors.add(selectorRef);
      const selector = analysis.selectors.find((item) => item.ref === selectorRef);
      const decision = selectorDecisions[selectorRef];
      if (!decision?.targetCategory) { errors.push(`${node.data.name}: falta resolver la categoría dinámica ${selector?.sourceCategory || selectorRef}`); return; }
      if (!['local', 'source'].includes(decision.mode)) { errors.push(`${node.data.name}: falta confirmar cómo usar la categoría dinámica ${selector?.sourceCategory || selectorRef}`); return; }
      if (selector && decision.mode === 'source') {
        selector.memberRefs.forEach((memberRef) => {
          const memberDecision = decisions[memberRef];
          if (!memberDecision) { errors.push(`${node.data.name}: falta resolver ${analysis.nodeByRef.get(memberRef)?.data?.name || memberRef} para conservar la categoría ${selector.sourceCategory}`); return; }
          if (memberDecision.action === 'omit') { errors.push(`${node.data.name}: ${analysis.nodeByRef.get(memberRef)?.data?.name || memberRef} no puede omitirse al reproducir la categoría ${selector.sourceCategory}`); return; }
          if (!effective.has(memberRef)) { effective.add(memberRef); if (memberDecision.action === 'create') queue.push(memberRef); }
        });
      }
    });
  }
  return { effective, usedSelectors };
};

const validateSelectorTargets = (analysis, decisions, selectorDecisions, effective, usedSelectors, errors, warnings) => {
  usedSelectors.forEach((selectorRef) => {
    const selector = analysis.selectors.find((item) => item.ref === selectorRef);
    const selectorDecision = selectorDecisions[selectorRef];
    const targetCategory = selectorDecision?.targetCategory || '';
    if (!selector || !targetCategory) return;
    if (selectorDecision.mode === 'source' && normalizeText(targetCategory) !== normalizeText(selector.sourceCategory)) errors.push(`El selector ${selector.sourceCategory}: reproducir origen debe conservar el nombre de categoría original.`);
    const localCategoryMembers = analysis.localProducts.filter((product) => productType(product) === 'insumo' && normalizeUnit(product.unit) === selector.unit && normalizeText(categoryValue(product)) === normalizeText(targetCategory));
    let available = localCategoryMembers.length;
    if (selectorDecision.mode === 'source' && localCategoryMembers.length > 0) warnings.push(`La categoría «${targetCategory}» ya existe con ${localCategoryMembers.length} insumos locales; esos productos también seguirán apareciendo en el selector.`);
    selector.memberRefs.forEach((ref) => {
      if (!effective.has(ref)) return;
      const decision = decisions[ref];
      const sourceNode = analysis.nodeByRef.get(ref);
      if (decision?.action === 'create' && normalizeUnit(sourceNode?.data?.unit) === selector.unit && normalizeText(sourceNode?.data?.category) === normalizeText(targetCategory)) available += 1;
      if (decision?.action === 'existing') {
        const local = analysis.localById.get(decision.targetId);
        if (local && normalizeText(categoryValue(local)) !== normalizeText(targetCategory)) {
          const message = `${sourceNode?.data?.name || ref} se reutiliza desde otra categoría y no formará parte de «${targetCategory}».`;
          if (selectorDecision.mode === 'source') errors.push(message);
          else warnings.push(message);
        }
      }
    });
    const groups = analysis.nodes.filter((node) => effective.has(node.ref) && decisions[node.ref]?.action === 'create').flatMap((node) => (node.data.config?.recipe?.choiceGroups || []).filter((group) => group.selectorRef === selectorRef).map((group) => ({ node, group })));
    groups.forEach(({ node, group }) => {
      if (group.min > 0 && available === 0) errors.push(`${node.data.name}: la categoría «${targetCategory}» quedaría sin opciones para un grupo obligatorio.`);
      if (group.defaultProductRef) {
        const d = decisions[group.defaultProductRef];
        const sourceNode = analysis.nodeByRef.get(group.defaultProductRef);
        const targetCategoryOfDefault = d?.action === 'existing' ? categoryValue(analysis.localById.get(d.targetId)) : sourceNode?.data?.category;
        if (targetCategoryOfDefault && normalizeText(targetCategoryOfDefault) !== normalizeText(targetCategory)) errors.push(`${node.data.name}: la opción predeterminada «${sourceNode?.data?.name || group.defaultProductRef}» no pertenece a la categoría destino «${targetCategory}».`);
      }
    });
  });
};

const salesIngredientCompatible = (product, unit, category) => {
  if (!activeProduct(product)) return false;
  const type = normalizeText(product.type);
  const normalizedCategory = normalizeText(categoryValue(product));
  const isIngredient = type === 'insumo' || (!type && ['insumo', 'insumos'].includes(normalizedCategory));
  return isIngredient && normalizeUnit(product.unit) === unit && normalizedCategory === category;
};

const validateRecipeRuntime = (product, productsById) => {
  const recipe = product?.config?.recipe;
  if (!plainObject(recipe)) return 'la receta no es un objeto válido';
  if (recipe.baseItems != null && !Array.isArray(recipe.baseItems)) return 'baseItems no es una lista válida';
  if (recipe.choiceGroups != null && !Array.isArray(recipe.choiceGroups)) return 'choiceGroups no es una lista válida';
  const baseItems = Array.isArray(recipe.baseItems) ? recipe.baseItems : [];
  const choiceGroups = Array.isArray(recipe.choiceGroups) ? recipe.choiceGroups : [];
  if (baseItems.length === 0 && choiceGroups.length === 0) return 'la receta está vacía';

  for (const item of baseItems) {
    if (!plainObject(item)) return 'la receta contiene un ingrediente fijo inválido';
    const id = String(item.productId || item.id || '').trim();
    const qty = Number(item.qty ?? item.quantity ?? 1);
    if (!id || !Number.isFinite(qty) || qty <= 0) return 'la receta contiene un ingrediente fijo inválido';
    const target = productsById.get(id);
    if (!activeProduct(target)) return `la receta usa un ingrediente que no está disponible (${id})`;
  }

  for (const group of choiceGroups) {
    if (!plainObject(group)) return 'la receta contiene un grupo dinámico inválido';
    const min = Number(group.min || 0);
    const max = Math.max(Number(group.max || 0), min);
    const perPickQty = Number(group.perPickQty || 0);
    const unit = normalizeUnit(group.unit || 'unidad');
    const category = normalizeText(group.fromCategory || '');
    if (!Number.isFinite(min) || min < 0 || !Number.isFinite(max) || max < min || (max > 0 && (!Number.isFinite(perPickQty) || perPickQty <= 0)) || (min > 0 && !category)) {
      return 'la receta contiene un grupo dinámico inválido';
    }
    const eligible = [...productsById.values()].filter((candidate) => salesIngredientCompatible(candidate, unit, category));
    if (min > eligible.length) return `la categoría «${group.fromCategory || ''}» no tiene suficientes ingredientes disponibles`;
    const defaultId = String(group.defaultProductId || '').trim();
    if (defaultId) {
      const defaultProduct = productsById.get(defaultId);
      if (!salesIngredientCompatible(defaultProduct, unit, category)) return 'la opción predeterminada de la receta no es válida para su categoría';
    }
  }
  return '';
};

const buildFutureProductsById = (analysis, plans) => {
  const productsById = new Map(analysis.localProducts.map((product) => [product.id, product]));
  plans.filter((plan) => plan.action === 'create' && plan.proposed).forEach((plan) => {
    productsById.set(plan.targetId, { ...plan.proposed, id: plan.targetId, deleted: false });
  });
  return productsById;
};

const validatePromotionBases = (analysis, decisions, effective, errors, plans, futureProductsById) => {
  const planByRef = new Map(plans.map((plan) => [plan.ref, plan]));
  analysis.nodes.forEach((node) => {
    if (!effective.has(node.ref) || decisions[node.ref]?.action !== 'create') return;
    const promo = node.data.config?.promotion;
    if (!promo?.sourceProductRef) return;
    if (node.data.config?.recipe) errors.push(`${node.data.name}: una promoción importada no puede conservar simultáneamente una receta propia; la configuración es ambigua.`);
    const quantity = Number(promo.quantity);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 50) {
      errors.push(`${node.data.name}: la cantidad de la promoción (${promo.quantity}) no es compatible con Cúcuta; debe estar entre 1 y 50.`);
    }
    const baseDecision = decisions[promo.sourceProductRef];
    const baseNode = analysis.nodeByRef.get(promo.sourceProductRef);
    let baseProduct = null;
    if (baseDecision?.action === 'existing') {
      baseProduct = analysis.localById.get(baseDecision.targetId);
    } else if (baseDecision?.action === 'create') {
      baseProduct = planByRef.get(promo.sourceProductRef)?.proposed || null;
      if (baseProduct) baseProduct = { ...baseProduct, id: `${NEW_ID_PREFIX}${promo.sourceProductRef}`, deleted: false };
    }
    if (!baseProduct || isPromotionProduct(baseProduct) || !plainObject(baseProduct?.config?.recipe)) {
      errors.push(`${node.data.name}: la base seleccionada «${baseProduct?.name || baseNode?.data?.name || promo.sourceProductRef}» no es compatible con esta promoción; debe ser un producto con receta válida y no otra promoción.`);
      return;
    }
    const recipeProblem = validateRecipeRuntime(baseProduct, futureProductsById);
    if (recipeProblem) errors.push(`${node.data.name}: la base «${baseProduct.name || baseNode?.data?.name || promo.sourceProductRef}» no es funcional para ventas en Cúcuta: ${recipeProblem}.`);
  });
};

const validateIceCreamRuntime = (analysis, decisions, effective, errors, warnings, plans, futureProductsById) => {
  const createdPlans = plans.filter((plan) => plan.action === 'create' && effective.has(plan.ref) && plan.proposed?.config?.isIceCream === true);
  const runtimeProducts = [...futureProductsById.values()].filter((product) => activeProduct(product));
  const unitSupplies = runtimeProducts.filter((product) => product.type === 'insumo' && product.unit === 'unidad');
  const gramSupplies = runtimeProducts.filter((product) => product.type === 'insumo' && product.unit === 'g');
  const lowerName = (product) => String(product?.name || '').toLowerCase();
  const hasUnitNamed = (token) => unitSupplies.some((product) => lowerName(product).includes(String(token || '').toLowerCase()));
  const flavors = gramSupplies.filter((product) => normalizeText(product.name).startsWith('helado'));

  createdPlans.forEach((plan) => {
    const cfg = plan.proposed.config || {};
    let baseProduct = null;
    if (cfg.baseInsumoId) baseProduct = unitSupplies.find((product) => product.id === cfg.baseInsumoId) || null;
    else {
      const preferred = String(cfg.base || 'vaso').toLowerCase();
      baseProduct = unitSupplies.find((product) => lowerName(product).includes(preferred)) || null;
    }
    if (!baseProduct) errors.push(`${plan.source.name}: no existe una base que el POS de helados pueda reconocer (type "insumo" y unit "unidad").`);
    if (Number(cfg.sabores || 1) > 0 && flavors.length === 0) errors.push(`${plan.source.name}: no hay sabores que el POS de Cúcuta pueda reconocer; requiere type "insumo", unit "g" y nombre que empiece por «helado».`);
    if (Number(cfg.servilletas || 0) > 0 && !hasUnitNamed('servillet')) warnings.push(`${plan.source.name}: el POS no encontrará una servilleta por sus reglas exactas (insumo/unidad y nombre que contenga «servillet»).`);
    if (Number(cfg.cucharas || 0) > 0 && !hasUnitNamed('cuchar')) warnings.push(`${plan.source.name}: el POS no encontrará una cuchara por sus reglas exactas (insumo/unidad y nombre que contenga «cuchar»).`);
    (Array.isArray(cfg.extraItems) ? cfg.extraItems : []).forEach((item) => {
      if (!futureProductsById.has(String(item.productId || ''))) errors.push(`${plan.source.name}: el extra «${item.name || item.productId || 'sin nombre'}» no existe en el catálogo futuro.`);
    });
    warnings.push(`${plan.source.name}: el POS de helados resuelve sabores y algunos consumibles por reglas exactas de tipo, unidad y nombre; revisa el resultado antes de una futura escritura.`);
  });
};

export const estimateProductImportPayloadBytes = (file, decisions = {}, selectorDecisions = {}) => utf8Bytes({ file, decisions, selectorDecisions });

export function buildProductImportPreview(analysis, decisions = {}, selectorDecisions = {}) {
  const errors = [], warnings = [];
  const { effective, usedSelectors } = collectEffectiveRefs(analysis, decisions, selectorDecisions, errors);
  const plans = [];
  effective.forEach((ref) => {
    const node = analysis.nodes.find((item) => item.ref === ref);
    const decision = decisions[ref];
    if (!node || !decision || decision.action === 'omit') return;
    if (decision.action === 'existing') {
      const target = analysis.localById.get(decision.targetId);
      if (!target) { errors.push(`${node.data.name}: el producto existente seleccionado ya no está disponible.`); return; }
      if (!compatible(node.data, target)) { errors.push(`${node.data.name}: el producto existente seleccionado tiene tipo o unidad incompatible.`); return; }
      plans.push({ ref, action: 'existing', source: node.data, targetId: target.id, targetName: target.name || target.id });
      return;
    }
    if (decision.action === 'create') plans.push({ ref, action: 'create', source: node.data, targetId: `${NEW_ID_PREFIX}${ref}`, targetName: node.data.name, proposed: reconstructCreateNode(node, decisions, selectorDecisions, analysis, errors) });
    else errors.push(`${node.data.name}: acción no soportada.`);
  });
  validateSelectorTargets(analysis, decisions, selectorDecisions, effective, usedSelectors, errors, warnings);
  const futureProductsById = buildFutureProductsById(analysis, plans);
  validatePromotionBases(analysis, decisions, effective, errors, plans, futureProductsById);
  validateIceCreamRuntime(analysis, decisions, effective, errors, warnings, plans, futureProductsById);

  const created = plans.filter((plan) => plan.action === 'create');
  const reused = plans.filter((plan) => plan.action === 'existing');
  if (created.length > TRANSFER_MAX_NEW_PRODUCTS) errors.push(`Esta transferencia intentaría crear ${created.length} productos; el máximo seguro por operación es ${TRANSFER_MAX_NEW_PRODUCTS}.`);
  if (estimateProductImportPayloadBytes(analysis.file, decisions, selectorDecisions) > TRANSFER_FILE_MAX_BYTES) errors.push('El archivo y las decisiones superan el límite seguro de 3.500.000 bytes. Reduce la transferencia.');
  if (!plans.length) errors.push('No hay productos para importar: todos los productos seleccionados fueron omitidos.');
  const reusedTargets = new Map();
  reused.forEach((plan) => {
    if (reusedTargets.has(plan.targetId)) warnings.push(`«${plan.source.name}» y «${reusedTargets.get(plan.targetId)}» se asociaron al mismo producto existente «${plan.targetName}». Revisa que sea intencional.`);
    else reusedTargets.set(plan.targetId, plan.source.name);
  });
  const createdBarcode = new Map();
  created.forEach((plan) => {
    const node = analysis.nodes.find((item) => item.ref === plan.ref);
    const barcode = cleanCode(plan.source.barcode);
    if (barcode) {
      const localMatches = analysis.localProducts.filter((product) => cleanCode(product.barcode) === barcode);
      if (localMatches.length) errors.push(`${plan.source.name}: no se puede crear con el código de barras ${barcode} porque ya lo usa ${localMatches.map((product) => product.name || product.id).join(', ')}.`);
      if (createdBarcode.has(barcode)) errors.push(`No se pueden crear varios productos con el mismo código de barras ${barcode}.`);
      createdBarcode.set(barcode, plan.ref);
    }
    if (!barcode && node?.highConfidenceCandidateIds?.length) {
      const names = node.highConfidenceCandidateIds.map((id) => analysis.localById.get(id)?.name || id);
      warnings.push(`${plan.source.name}: se creará como nuevo aunque existe ${names.length === 1 ? 'una coincidencia muy alta' : `${names.length} coincidencias muy altas`} en esta sede (${names.join(', ')}).`);
    }
  });
  const ignored = analysis.nodes.filter((node) => !effective.has(node.ref)).map((node) => node.ref);
  return {
    valid: errors.length === 0,
    errors: [...new Set(errors)],
    warnings: [...new Set(warnings)],
    effectiveRefs: [...effective],
    ignoredRefs: ignored,
    usedSelectors: [...usedSelectors],
    plans,
    summary: {
      selected: analysis.file.products.length,
      effective: plans.length,
      create: created.length,
      existing: reused.length,
      omittedRoots: analysis.roots.filter((ref) => decisions[ref]?.action === 'omit').length,
      ignoredDependencies: ignored.filter((ref) => !analysis.roots.includes(ref)).length,
      recipes: created.filter((plan) => !!plan.proposed?.config?.recipe).length,
      promotions: created.filter((plan) => !!plan.proposed?.config?.promotion).length,
      categories: usedSelectors.size,
    },
  };
}
